import { useMemo } from "react";
import {
  format,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  parseISO,
  differenceInMinutes,
  differenceInCalendarDays,
  setHours,
  setMinutes,
} from "date-fns";
import type { CalendarEvent, Calendar } from "@/hooks/useCalendar";

interface CalendarWeekViewProps {
  currentDate: Date;
  events: CalendarEvent[];
  calendars: Calendar[];
  onEventClick: (event: CalendarEvent) => void;
}

const START_HOUR = 6;
const END_HOUR = 22; // 10 PM
const HOURS = Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => START_HOUR + i);
const HOUR_HEIGHT = 60; // px per hour

export function CalendarWeekView({
  currentDate,
  events,
  calendars,
  onEventClick,
}: CalendarWeekViewProps) {
  const calendarMap = useMemo(() => {
    const map: Record<string, Calendar> = {};
    for (const c of calendars) map[c.id] = c;
    return map;
  }, [calendars]);

  const weekStart = startOfWeek(currentDate);
  const weekEnd = endOfWeek(currentDate);
  const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

  // Separate all-day vs timed
  const { allDayEvents, timedEvents } = useMemo(() => {
    const allDay: CalendarEvent[] = [];
    const timed: CalendarEvent[] = [];
    for (const ev of events) {
      if (ev.all_day || differenceInCalendarDays(parseISO(ev.end_at), parseISO(ev.start_at)) >= 1) {
        allDay.push(ev);
      } else {
        timed.push(ev);
      }
    }
    return { allDayEvents: allDay, timedEvents: timed };
  }, [events]);

  const getTimedForDay = (day: Date) =>
    timedEvents.filter((ev) => isSameDay(parseISO(ev.start_at), day));

  const getAllDayForDay = (day: Date) =>
    allDayEvents.filter((ev) => {
      const s = parseISO(ev.start_at);
      const e = parseISO(ev.end_at);
      return day >= new Date(s.getFullYear(), s.getMonth(), s.getDate()) &&
        day < new Date(e.getFullYear(), e.getMonth(), e.getDate() + (ev.all_day ? 0 : 1));
    });

  const getEventPosition = (ev: CalendarEvent, day: Date) => {
    const start = parseISO(ev.start_at);
    const end = parseISO(ev.end_at);
    const dayStart = setMinutes(setHours(day, START_HOUR), 0);
    const topMin = Math.max(0, differenceInMinutes(start, dayStart));
    const duration = Math.max(15, differenceInMinutes(end, start));
    return {
      top: (topMin / 60) * HOUR_HEIGHT,
      height: Math.max(20, (duration / 60) * HOUR_HEIGHT),
    };
  };

  const now = new Date();
  const isThisWeek = days.some((d) => isSameDay(d, now));

  return (
    <div className="border rounded-lg overflow-hidden bg-card h-full flex flex-col">
      {/* Single scroll container — header is sticky so columns always align */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {/* Header with day labels */}
        <div className="grid grid-cols-[64px_repeat(7,1fr)] border-b sticky top-0 z-10 bg-card shadow-[0_2px_4px_-1px_rgba(0,0,0,0.1)]">
          <div className="border-r" />
          {days.map((day) => {
            const isToday = isSameDay(day, now);
            return (
              <div
                key={day.toISOString()}
                className={`text-center py-2 border-r last:border-r-0 ${
                  isToday ? "bg-primary/5" : ""
                }`}
              >
                <div className="text-xs text-muted-foreground">
                  {format(day, "EEE")}
                </div>
                <div
                  className={`text-lg font-semibold ${
                    isToday
                      ? "bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center mx-auto"
                      : ""
                  }`}
                >
                  {format(day, "d")}
                </div>
              </div>
            );
          })}
        </div>

        {/* All-day row */}
        {allDayEvents.length > 0 && (
          <div className="grid grid-cols-[64px_repeat(7,1fr)] border-b sticky top-[60px] z-10 bg-card">
            <div className="text-[10px] text-muted-foreground text-right pr-2 py-1 border-r">
              all-day
            </div>
            {days.map((day) => {
              const dayAllDay = getAllDayForDay(day);
              return (
                <div
                  key={day.toISOString()}
                  className="border-r last:border-r-0 p-0.5 space-y-0.5 min-h-[28px]"
                >
                  {dayAllDay.map((ev) => {
                    const cal = calendarMap[ev.calendar_id];
                    const isOOO = calendarMap[ev.calendar_id]?.slug === "cal_ooo";
                    return (
                      <div
                        key={ev.id}
                        className="text-[10px] text-white px-1 py-0.5 rounded truncate cursor-pointer hover:opacity-80"
                        style={{
                          backgroundColor: cal?.color || "#3b82f6",
                          backgroundImage: isOOO
                            ? "repeating-linear-gradient(45deg,transparent,transparent 4px,rgba(255,255,255,0.25) 4px,rgba(255,255,255,0.25) 5px)"
                            : undefined,
                        }}
                        onClick={() => onEventClick(ev)}
                        title={ev.title}
                      >
                        {isOOO ? "Out · " : ""}{ev.title}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

        {/* Hourly grid */}
        <div
          className="grid grid-cols-[64px_repeat(7,1fr)] relative"
          style={{ height: HOURS.length * HOUR_HEIGHT }}
        >
          {/* Hour labels */}
          <div className="border-r relative">
            {HOURS.map((hour) => (
              <div
                key={hour}
                className="absolute text-[10px] text-muted-foreground text-right pr-2 w-full"
                style={{ top: (hour - START_HOUR) * HOUR_HEIGHT + 4 }}
              >
                {format(setHours(new Date(), hour), "ha").toLowerCase()}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {days.map((day) => {
            const isToday = isSameDay(day, now);
            const dayEvents = getTimedForDay(day);

            return (
              <div
                key={day.toISOString()}
                className={`border-r last:border-r-0 relative ${
                  isToday ? "bg-primary/5" : ""
                }`}
              >
                {/* Hour lines */}
                {HOURS.map((hour) => (
                  <div
                    key={hour}
                    className="absolute w-full border-t border-border/50"
                    style={{ top: (hour - START_HOUR) * HOUR_HEIGHT }}
                  />
                ))}

                {/* Current time marker */}
                {isToday && isThisWeek && (() => {
                  const minutesSinceStart =
                    (now.getHours() - START_HOUR) * 60 + now.getMinutes();
                  if (minutesSinceStart < 0 || minutesSinceStart > (END_HOUR - START_HOUR) * 60) return null;
                  const top = (minutesSinceStart / 60) * HOUR_HEIGHT;
                  return (
                    <div
                      className="absolute left-0 right-0 z-20 flex items-center"
                      style={{ top }}
                    >
                      <div className="h-2 w-2 rounded-full bg-red-500 -ml-1" />
                      <div className="flex-1 border-t border-red-500" />
                    </div>
                  );
                })()}

                {/* Timed events */}
                {dayEvents.map((ev) => {
                  const pos = getEventPosition(ev, day);
                  const cal = calendarMap[ev.calendar_id];
                  const isOOO = calendarMap[ev.calendar_id]?.slug === "cal_ooo";
                  return (
                    <div
                      key={ev.id}
                      className="absolute left-0.5 right-0.5 rounded px-1 py-0.5 overflow-hidden cursor-pointer hover:opacity-80 z-10"
                      style={{
                        top: pos.top,
                        height: pos.height,
                        backgroundColor: cal?.color || "#3b82f6",
                        backgroundImage: isOOO
                          ? "repeating-linear-gradient(45deg,transparent,transparent 4px,rgba(255,255,255,0.25) 4px,rgba(255,255,255,0.25) 5px)"
                          : undefined,
                        opacity: 0.9,
                      }}
                      onClick={() => onEventClick(ev)}
                      title={ev.title}
                    >
                      <div className="text-[10px] font-medium text-white leading-tight truncate">
                        {isOOO ? "Out · " : ""}{ev.title}
                      </div>
                      <div className="text-[9px] text-white/80 truncate">
                        {format(parseISO(ev.start_at), "h:mma").toLowerCase()}
                        {" – "}
                        {format(parseISO(ev.end_at), "h:mma").toLowerCase()}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
